import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload as UploadIcon, FileSpreadsheet, Check, AlertTriangle, Download, ArrowLeft, Wand2, Linkedin, Users as UsersIcon, UserX } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams, useNavigate } from "react-router-dom";
import ImportHistory from "@/components/ImportHistory";
import UnmatchedAssignees from "@/components/UnmatchedAssignees";

const TEMPLATE = `name,phone,email,company,designation,budget,notes
Rahul Verma,+91 9876543210,rahul@example.com,Acme Corp,VP Sales,150000,Interested in enterprise plan
`;

const LEAD_FIELDS = [
  { key: "name",        label: "Name",        required: true  },
  { key: "phone",       label: "Phone",       required: false },
  { key: "email",       label: "Email",       required: false },
  { key: "company",     label: "Company",     required: false },
  { key: "designation", label: "Designation", required: false },
  { key: "budget",      label: "Budget",      required: false },
  { key: "notes",       label: "Notes",       required: false },
];

const SKIP = "__skip__";

export default function Upload() {
  const { user } = useAuth();
  const isAdmin = user?.role === "superadmin";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const linkedInMode = searchParams.get("linkedin") === "1";
  const [step, setStep] = useState(1); // 1 = pick file, 2 = map fields, 3 = result
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [colorMapping, setColorMapping] = useState({});
  const [assigneeMap, setAssigneeMap] = useState({});
  const [assignTo, setAssignTo] = useState("");
  const [members, setMembers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    if (isAdmin) api.get("/users").then((r) => setMembers(r.data)).catch(() => {});
  }, [isAdmin]);

  const goPreview = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/leads/upload-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      setMapping(data.suggested_mapping || {});
      // Prime the assignee map with auto-matched user IDs so SuperAdmin can just glance & tweak.
      const nextAssigneeMap = {};
      (data.assignee_matches || []).forEach((m) => {
        if (m.user_id) nextAssigneeMap[m.value] = m.user_id;
      });
      setAssigneeMap(nextAssigneeMap);
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const runImport = async () => {
    if (!preview) return;
    if (!mapping.name || mapping.name === SKIP) {
      toast.error("Please map the 'Name' field — it's required.");
      return;
    }
    setBusy(true);
    try {
      const cleanMapping = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (v && v !== SKIP) cleanMapping[k] = v;
      }
      const fd = new FormData();
      // Prefer cached upload_id from the preview step; fall back to re-uploading the file.
      if (preview.upload_id) {
        fd.append("upload_id", preview.upload_id);
      } else if (file) {
        fd.append("file", file);
      }
      fd.append("mapping", JSON.stringify(cleanMapping));
      if (assignTo) fd.append("assign_to", assignTo);
      const cleanColors = Object.fromEntries(
        Object.entries(colorMapping).filter(([, v]) => v && String(v).trim())
      );
      if (Object.keys(cleanColors).length) {
        fd.append("color_mapping", JSON.stringify(cleanColors));
      }
      // Per-row auto-assign — only send if the preview surfaced an assignee column.
      if (preview.assignee_column) {
        fd.append("assignee_column", preview.assignee_column);
        const cleanAssignees = Object.fromEntries(
          Object.entries(assigneeMap).filter(([, v]) => v && String(v).trim())
        );
        if (Object.keys(cleanAssignees).length) {
          fd.append("assignee_map", JSON.stringify(cleanAssignees));
        }
      }
      if (linkedInMode) fd.append("force_source", "LinkedIn");
      const { data } = await api.post("/leads/upload-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setStep(3);
      setHistoryKey((k) => k + 1);
      toast.success(`Imported ${data.inserted} leads`);
      if (linkedInMode && data.inserted > 0) {
        // After LinkedIn CSV import, jump back to the Inbox so SuperAdmin can allocate.
        setTimeout(() => navigate("/dashboard/linkedin"), 900);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setMapping({}); setColorMapping({}); setAssigneeMap({}); setResult(null); setAssignTo("");
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "leads_template.csv"; a.click();
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="upload-page">
      <header>
        <p className="text-xs uppercase tracking-widest text-slate-500">Bulk Import</p>
        <h1 className="text-xl font-semibold mt-1 text-slate-900">Upload Leads</h1>
        <p className="text-slate-500 mt-1 text-sm">CSV or Excel — map your columns to lead fields, then import.</p>
      </header>

      {linkedInMode && (
        <div className="rounded-xl border border-[#0A66C2]/25 bg-[#0A66C2]/5 p-4 flex items-start gap-3" data-testid="linkedin-mode-banner">
          <span className="w-9 h-9 rounded-lg bg-[#0A66C2] flex items-center justify-center flex-shrink-0">
            <Linkedin className="w-4 h-4 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-800">LinkedIn Import mode</p>
            <p className="text-xs text-slate-600 mt-0.5">
              Every row from this file will be imported with <code className="text-[10px] px-1 py-0.5 rounded bg-white border border-slate-200">source=LinkedIn</code>, unassigned, and dropped into the SuperAdmin's LinkedIn Inbox. Perfect for Sales Navigator exports.
            </p>
          </div>
          <button onClick={() => navigate("/dashboard/upload")} className="text-xs text-slate-500 hover:text-slate-800 flex-shrink-0" data-testid="exit-linkedin-mode">Exit</button>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="upload-stepper">
        {["Choose file", "Map fields", "Done"].map((label, i) => {
          const idx = i + 1;
          const active = step === idx;
          const done = step > idx;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center flex-shrink-0
                ${done ? "bg-emerald-500 text-white" : active ? "bg-[#1D63ED] text-white" : "bg-slate-200 text-slate-500"}`}>
                {done ? "✓" : idx}
              </div>
              <span className={`text-xs ${active ? "text-slate-900 font-medium" : "text-slate-500"}`}>{label}</span>
              {idx < 3 && <span className="mx-1 sm:mx-2 w-4 sm:w-8 h-px bg-slate-300 flex-shrink-0" />}
            </div>
          );
        })}
      </div>

      {/* STEP 1 */}
      {step === 1 && (
        <div className="solid-panel p-6">
          <label htmlFor="file-input" className="border-2 border-dashed border-slate-200 hover:border-[#1D63ED]/60 rounded-lg p-8 flex flex-col items-center justify-center gap-3 cursor-pointer transition-colors block" data-testid="file-dropzone">
            <div className="w-12 h-12 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
              <UploadIcon className="w-5 h-5 text-[#1D63ED]" />
            </div>
            <p className="text-base font-semibold text-slate-800">Drop or select a file</p>
            <p className="text-xs text-slate-500">CSV or XLSX up to 5MB</p>
            <input id="file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="file-input" />
            {file && (
              <div className="mt-2 bg-white border border-slate-200 px-3 py-1.5 rounded flex items-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-[#1D63ED]" />
                <span className="text-sm">{file.name}</span>
                <span className="text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            )}
          </label>
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download Template
            </Button>
            <Button onClick={goPreview} disabled={!file || busy} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="preview-btn">
              {busy ? "Reading…" : "Next: Map fields →"}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && preview && (
        <div className="space-y-4" data-testid="mapping-step">
          <div className="solid-panel p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wand2 className="w-4 h-4 text-[#1D63ED]" />
              <p className="text-sm font-semibold text-slate-800">Auto-detected mapping — please review</p>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              Found <strong>{preview.row_count}</strong> row{preview.row_count !== 1 ? "s" : ""} with {preview.columns.length} column{preview.columns.length !== 1 ? "s" : ""}. Match each Lead field to a column below (or Skip).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {LEAD_FIELDS.map((f) => (
                <div key={f.key} className="flex items-center gap-3 py-1.5" data-testid={`map-row-${f.key}`}>
                  <div className="w-40 flex-shrink-0">
                    <p className="text-sm font-medium text-slate-800">
                      {f.label}
                      {f.required && <span className="text-rose-500 ml-0.5">*</span>}
                    </p>
                    <p className="text-[11px] text-slate-500">Lead.{f.key}</p>
                  </div>
                  <Select value={mapping[f.key] || SKIP} onValueChange={(v) => setMapping((m) => ({ ...m, [f.key]: v }))}>
                    <SelectTrigger className="h-9 bg-white border-slate-200 flex-1" data-testid={`map-select-${f.key}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SKIP}>— Skip —</SelectItem>
                      {preview.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            {isAdmin && (
              <div className="mt-5 pt-4 border-t border-slate-200 flex items-center gap-3">
                <p className="text-sm font-medium text-slate-800 w-40 flex-shrink-0">Assign all to</p>
                <Select value={assignTo || SKIP} onValueChange={(v) => setAssignTo(v === SKIP ? "" : v)}>
                  <SelectTrigger className="h-9 bg-white border-slate-200 flex-1" data-testid="assign-all-select">
                    <SelectValue placeholder="Default — myself" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SKIP}>— Me (SuperAdmin) —</SelectItem>
                    {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Preview table */}
          <div className="solid-panel p-5">
            <p className="text-sm font-semibold text-slate-800 mb-3">Preview — first {preview.preview.length} row(s)</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    {preview.row_colors_sample?.length ? <th className="w-1 px-2" aria-label="Row colour" /> : null}
                    {preview.columns.map((c) => (<th key={c} className="text-left px-3 py-2 whitespace-nowrap">{c}</th>))}
                  </tr>
                </thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={i} className="border-t border-slate-200/60">
                      {preview.row_colors_sample?.length ? (
                        <td className="w-1 px-0" data-testid={`preview-row-color-${i}`}>
                          {preview.row_colors_sample[i] ? (
                            <span className="inline-block w-2 h-6 rounded-sm" style={{ background: preview.row_colors_sample[i] }} title={preview.row_colors_sample[i]} />
                          ) : null}
                        </td>
                      ) : null}
                      {preview.columns.map((c) => (<td key={c} className="px-3 py-2 whitespace-nowrap text-slate-700">{row[c] || <span className="text-slate-300">—</span>}</td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Colour classification panel */}
          {preview.color_summary && Object.keys(preview.color_summary).length > 0 && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-5" data-testid="color-mapping-panel">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 rounded-md bg-indigo-100 border border-indigo-200 flex items-center justify-center">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400" />
                </span>
                <p className="text-sm font-semibold text-slate-800">Colours detected in your sheet</p>
                <span className="text-[11px] text-slate-500">{Object.keys(preview.color_summary).length} colour(s)</span>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                We spotted cells with fill colours. Give each colour a tag name and every matching row will be imported with that tag — perfect for keeping your colour-coded classification (e.g. red&nbsp;=&nbsp;hot, green&nbsp;=&nbsp;converted). Leave blank to ignore a colour.
              </p>
              <div className="space-y-2">
                {Object.entries(preview.color_summary).map(([hex, count]) => (
                  <div key={hex} className="flex items-center gap-3 flex-wrap" data-testid={`color-row-${hex.replace("#","")}`}>
                    <span className="inline-block w-6 h-6 rounded border border-slate-300 flex-shrink-0" style={{ background: hex }} title={hex} />
                    <span className="text-xs text-slate-600 font-mono w-20">{hex}</span>
                    <span className="text-xs text-slate-500 w-24">{count} row{count !== 1 ? "s" : ""}</span>
                    <input
                      type="text"
                      placeholder="Tag name (e.g. hot, converted, warm)"
                      value={colorMapping[hex] || ""}
                      onChange={(e) => setColorMapping((m) => ({ ...m, [hex]: e.target.value }))}
                      className="flex-1 min-w-[180px] h-9 px-3 rounded-md bg-white border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1D63ED]/30"
                      data-testid={`color-tag-input-${hex.replace("#","")}`}
                    />
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Tip: only .xlsx files carry cell colours — CSV imports skip this step.
              </p>
            </div>
          )}

          {/* Assignee column detection — show a review panel so SuperAdmin can tweak matches */}
          {isAdmin && preview.assignee_column && preview.assignee_matches?.length > 0 && (
            <div className="rounded-xl border border-sky-200 bg-sky-50/40 p-5" data-testid="assignee-mapping-panel">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="w-6 h-6 rounded-md bg-sky-100 border border-sky-200 flex items-center justify-center">
                  <UsersIcon className="w-3.5 h-3.5 text-sky-600" />
                </span>
                <p className="text-sm font-semibold text-slate-800">Auto-assign from "{preview.assignee_column}"</p>
                <span className="text-[11px] text-slate-500">
                  {preview.assignee_matches.filter((m) => m.matched).length}/{preview.assignee_matches.length} auto-matched
                </span>
              </div>
              <p className="text-xs text-slate-600 mb-3">
                We spotted an assignee column. Each value below was matched against your team — review, pick a different
                member from the dropdown, or leave <em>Unassigned</em> for leads you'll assign later.
              </p>
              <div className="space-y-2">
                {preview.assignee_matches.map((m) => {
                  const chosen = assigneeMap[m.value] || "";
                  const isMatched = !!chosen;
                  const isRemembered = m.source === "alias";
                  return (
                    <div key={m.value} className="flex items-center gap-3 flex-wrap" data-testid={`assignee-row-${m.value}`}>
                      <div className="flex items-center gap-2 w-56 flex-shrink-0">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isMatched ? "bg-emerald-500" : "bg-rose-400"}`} />
                        <span className="text-sm text-slate-800 truncate" title={m.value}>{m.value}</span>
                        {isRemembered && (
                          <span
                            className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-200 flex-shrink-0"
                            title="Auto-matched from a saved alias"
                            data-testid={`assignee-alias-tag-${m.value}`}
                          >
                            saved
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 w-20 flex-shrink-0">{m.count} row{m.count !== 1 ? "s" : ""}</span>
                      <Select
                        value={chosen || SKIP}
                        onValueChange={(v) => setAssigneeMap((prev) => ({ ...prev, [m.value]: v === SKIP ? "" : v }))}
                      >
                        <SelectTrigger className="flex-1 min-w-[200px] h-9 bg-white border-slate-200" data-testid={`assignee-select-${m.value}`}>
                          <SelectValue placeholder="— Leave unassigned —" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={SKIP}>— Leave unassigned —</SelectItem>
                          {members.map((mem) => <SelectItem key={mem.id} value={mem.id}>{mem.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-500 mt-3">
                Tip: unassigned rows show up in a "needs manual assignment" list on the next step, so nothing slips through.
              </p>
            </div>
          )}

          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="ghost" onClick={() => setStep(1)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button onClick={runImport} disabled={busy} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="import-btn">
              {busy ? "Importing…" : `Import ${preview.row_count} lead${preview.row_count !== 1 ? "s" : ""}`}
            </Button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && result && (
        <div className="space-y-4" data-testid="upload-result">
          <div className="solid-panel p-6 space-y-2">
            <div className="flex items-center gap-2 text-emerald-600">
              <Check className="w-5 h-5" />
              <p className="text-xl font-semibold">Imported {result.inserted} lead{result.inserted !== 1 ? "s" : ""}</p>
            </div>
            {result.duplicates?.length > 0 && (
              <div className="text-sm text-amber-700 space-y-1 mt-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{result.duplicates.length} duplicate row(s) skipped</span>
                </div>
                <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
                  {result.duplicates.slice(0, 10).map((d, i) => <li key={i}>Row {d.row}: <strong>{d.name}</strong> matches existing lead <em>{d.matched_lead}</em></li>)}
                  {result.duplicates.length > 10 && <li className="text-slate-400">…and {result.duplicates.length - 10} more</li>}
                </ul>
              </div>
            )}
            {result.errors?.length > 0 && (
              <div className="text-sm text-rose-700 space-y-1 mt-3">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4" />
                  <span>{result.errors.length} row(s) skipped due to errors</span>
                </div>
                <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
                  {result.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
          </div>

          {result.unmatched_assignees?.length > 0 && (
            <UnmatchedAssignees
              rows={result.unmatched_assignees}
              members={members}
              onDone={() => setResult((r) => ({ ...r, unmatched_assignees: [] }))}
            />
          )}

          <Button onClick={reset} className="bg-[#1D63ED] hover:bg-[#1854D9] text-white" data-testid="upload-again-btn">
            Upload another file
          </Button>
        </div>
      )}

      <ImportHistory refreshKey={historyKey} onChanged={() => setHistoryKey((k) => k + 1)} />
    </div>
  );
}
